/**
 * Cookie utilities for proper cookie management
 * Replaces localStorage usage for cookie consent and preferences
 */

/**
 * Set a cookie with proper attributes
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {number} days - Expiration in days (default: 365)
 * @param {object} options - Additional cookie options
 */
export const setCookie = (name, value, days = 365, options = {}) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  
  const cookieOptions = {
    expires: expires.toUTCString(),
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'Lax',
    ...options
  };
  
  let cookieString = `${name}=${encodeURIComponent(value)}`;
  
  Object.entries(cookieOptions).forEach(([key, val]) => {
    if (val === true) {
      cookieString += `; ${key}`;
    } else if (val !== false && val !== null && val !== undefined) {
      cookieString += `; ${key}=${val}`;
    }
  });
  
  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length, c.length));
    }
  }
  return null;
};

/**
 * Delete a cookie
 * @param {string} name - Cookie name
 * @param {string} path - Cookie path (default: '/')
 */
export const deleteCookie = (name, path = '/') => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
};

/**
 * Get cookie consent preferences
 * @returns {object|null} Cookie preferences or null if not set
 */
export const getCookieConsent = () => {
  const consent = getCookie('cookie-consent');
  if (!consent) return null;
  
  try {
    return JSON.parse(consent);
  } catch (error) {
    // If cookie contains invalid JSON, delete it and return null
    deleteCookie('cookie-consent');
    deleteCookie('cookie-consent-date');
    return null;
  }
};

/**
 * Set cookie consent preferences
 * @param {object} preferences - Cookie preferences object
 */
export const setCookieConsent = (preferences) => {
  setCookie('cookie-consent', JSON.stringify(preferences), 365);
  setCookie('cookie-consent-date', new Date().toISOString(), 365);
};

/**
 * Check if user has given consent for a specific cookie type
 * @param {string} type - Cookie type ('necessary', 'analytics', 'marketing', 'functional')
 * @returns {boolean} True if consent given
 */
export const hasConsentFor = (type) => {
  const consent = getCookieConsent();
  if (!consent) return false;
  
  // Necessary cookies are always allowed
  if (type === 'necessary') return true;
  
  return consent[type] === true;
};
